import { DefaultRegistrationConfig } from './registrationConfig.constants';
import {
  RegistrationConfigState,
  RegistrationStep,
} from './registrationConfig.types';
import {
  createAsyncThunk,
  createSlice,
  injectSlice,
  PayloadAction,
} from '@bonanza-social/toolkit/state';
import { RegistrationConfigThreeSteps } from './registrationConfigThreeStep.constants';
import {
  getOrAssignRegVariant,
  RegistrationVariantEnum,
} from '../../utils/fetchRegistrationVariant';

export const fetchRegistrationConfig = createAsyncThunk(
  'registration/config',
  () => {
    const variant = getOrAssignRegVariant();

    const data =
      variant === RegistrationVariantEnum.threeStep
        ? RegistrationConfigThreeSteps
        : DefaultRegistrationConfig;

    return { data, variant };
  },
);

const initialState: RegistrationConfigState = {
  data: DefaultRegistrationConfig,
  dataState: 'idle',
  variant: RegistrationVariantEnum.elevenStep,
};

const registrationConfig = createSlice({
  name: 'registrationConfig',
  initialState,
  reducers: {
    removeStep(state, action: PayloadAction<RegistrationStep['id']>) {
      state.data.steps = state.data.steps.filter(
        (step) => step.id !== action.payload,
      );
    },
    skipStep(state, action: PayloadAction<RegistrationStep['id']>) {
      state.data.steps = state.data.steps.map((step) =>
        step.id === action.payload ? { ...step, skip: true } : step,
      );
    },
    restoreStep(state, action: PayloadAction<RegistrationStep['id']>) {
      state.data.steps = state.data.steps.map((step) =>
        step.id === action.payload ? { ...step, skip: false } : step,
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRegistrationConfig.pending, (state) => {
      state.dataState = 'pending';
    });
    builder.addCase(fetchRegistrationConfig.fulfilled, (state, action) => {
      state.dataState = 'fulfilled';
      state.data = action.payload.data;
      state.variant = action.payload.variant;
    });
    builder.addCase(fetchRegistrationConfig.rejected, (state) => {
      state.dataState = 'rejected';
    });
  },
});

export const withRegistrationConfig = injectSlice({
  reducerPath: 'registrationConfig',
  reducer: registrationConfig.reducer,
});

export const { removeStep, skipStep, restoreStep } = registrationConfig.actions;

export const selectRegistrationConfig = withRegistrationConfig.selector(
  (state) => state.registrationConfig.data,
);

export const selectRegistrationConfigDataState =
  withRegistrationConfig.selector(
    (state) => state.registrationConfig.dataState,
  );

export const selectRegistrationConfigVariant = withRegistrationConfig.selector(
  (state) => state.registrationConfig.variant,
);

declare global {
  interface LazyLoadedSlices {
    registrationConfig: ReturnType<typeof registrationConfig.reducer>;
  }
}
